/**
 * Add Activity Modal
 *
 * Modal for creating or editing activities
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  Users,
  Mail,
  Search,
  Presentation,
  StickyNote,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ACTIVITY_TYPES } from '../../constants';
import type { CrmActivityDetail, CrmActivityType, CreateCrmActivityInput } from '../../types';

interface AddActivityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity?: CrmActivityDetail | null;
  defaultType?: CrmActivityType;
  onSave: (data: CreateCrmActivityInput) => Promise<void>;
  isSaving?: boolean;
}

const ACTIVITY_ICONS: Record<CrmActivityType, React.ComponentType<{ className?: string }>> = {
  call: Phone,
  meeting: Users,
  email: Mail,
  discovery_session: Search,
  proposal_presentation: Presentation,
  note: StickyNote,
};

const DURATION_OPTIONS = [
  { value: '15', key: '15min' },
  { value: '30', key: '30min' },
  { value: '45', key: '45min' },
  { value: '60', key: '1h' },
  { value: '90', key: '1h30' },
  { value: '120', key: '2h' },
];

export function AddActivityModal({
  open,
  onOpenChange,
  activity,
  defaultType,
  onSave,
  isSaving,
}: AddActivityModalProps) {
  const { t } = useTranslation('crm');
  const isEditing = !!activity;

  const [formData, setFormData] = useState({
    title: '',
    type: defaultType || ('call' as CrmActivityType),
    scheduledAt: '',
    scheduledTime: '',
    durationMinutes: '30',
    description: '',
  });

  // Reset form when modal opens or activity changes
  useEffect(() => {
    if (open) {
      if (activity) {
        // Editing mode - populate from activity
        const scheduledDate = activity.scheduled_at
          ? new Date(activity.scheduled_at).toISOString().split('T')[0]
          : '';
        const scheduledTime = activity.scheduled_at
          ? new Date(activity.scheduled_at).toTimeString().slice(0, 5)
          : '';

        setFormData({
          title: activity.title,
          type: activity.type,
          scheduledAt: scheduledDate,
          scheduledTime: scheduledTime,
          durationMinutes: activity.duration_minutes?.toString() || '30',
          description: activity.description || '',
        });
      } else {
        // Creating mode - reset form
        setFormData({
          title: '',
          type: defaultType || 'call',
          scheduledAt: new Date().toISOString().split('T')[0],
          scheduledTime: '',
          durationMinutes: '30',
          description: '',
        });
      }
    }
  }, [open, activity, defaultType]);

  const handleTypeSelect = (type: CrmActivityType) => {
    setFormData((prev) => ({ ...prev, type }));
    // Set suggested duration from activity type
    const typeDef = ACTIVITY_TYPES.find((t) => t.key === type);
    if (typeDef && typeDef.suggestedDuration > 0) {
      setFormData((prev) => ({
        ...prev,
        type,
        durationMinutes: typeDef.suggestedDuration.toString(),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Combine date and time for scheduled_at
    let scheduledAt: string | undefined;
    if (formData.scheduledAt) {
      if (formData.scheduledTime) {
        scheduledAt = new Date(`${formData.scheduledAt}T${formData.scheduledTime}`).toISOString();
      } else {
        scheduledAt = new Date(`${formData.scheduledAt}T09:00:00`).toISOString();
      }
    }

    await onSave({
      title: formData.title,
      type: formData.type,
      scheduled_at: scheduledAt,
      duration_minutes: parseInt(formData.durationMinutes, 10) || undefined,
      description: formData.description || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? t('activities.addModal.titleEdit') : t('activities.addModal.titleCreate')}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? t('activities.addModal.descriptionEdit')
                : t('activities.addModal.descriptionCreate')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Activity Type Selector */}
            <div className="space-y-2">
              <Label>{t('activities.addModal.activityType')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {ACTIVITY_TYPES.map((type) => {
                  const Icon = ACTIVITY_ICONS[type.key];
                  const isSelected = formData.type === type.key;
                  return (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => handleTypeSelect(type.key)}
                      className={cn(
                        'flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors',
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5',
                          isSelected ? 'text-primary' : 'text-gray-500'
                        )}
                      />
                      <span
                        className={cn(
                          'text-xs font-medium',
                          isSelected ? 'text-primary' : 'text-gray-600'
                        )}
                      >
                        {t(`activities.types.${type.key}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{t('activities.addModal.titleLabel')} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder={t('activities.addModal.titlePlaceholder')}
                required
                disabled={isSaving}
              />
            </div>

            {/* Date and Time (side by side) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduledAt">{t('activities.addModal.date')}</Label>
                <Input
                  id="scheduledAt"
                  type="date"
                  value={formData.scheduledAt}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduledAt: e.target.value }))
                  }
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduledTime">{t('activities.addModal.time')}</Label>
                <Input
                  id="scheduledTime"
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduledTime: e.target.value }))
                  }
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* Duration */}
            {formData.type !== 'note' && (
              <div className="space-y-2">
                <Label htmlFor="duration">{t('activities.addModal.duration')}</Label>
                <Select
                  value={formData.durationMinutes}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, durationMinutes: value }))
                  }
                  disabled={isSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {t(`activities.durations.${opt.key}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description / Notes */}
            <div className="space-y-2">
              <Label htmlFor="description">
                {formData.type === 'note'
                  ? t('activities.addModal.notesLabel')
                  : t('activities.addModal.descriptionLabel')}
              </Label>
              <RichTextEditor
                value={formData.description}
                onChange={(html) =>
                  setFormData((prev) => ({ ...prev, description: html }))
                }
                placeholder={
                  formData.type === 'note'
                    ? t('activities.addModal.notesPlaceholder')
                    : t('activities.addModal.descriptionPlaceholder')
                }
                disabled={isSaving}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              {t('buttons.cancel')}
            </Button>
            <Button type="submit" disabled={isSaving || !formData.title}>
              {isSaving
                ? t('activities.addModal.saving')
                : isEditing
                  ? t('activities.addModal.saveChanges')
                  : t('activities.addModal.logActivity')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
